/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2020, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import * as _ from 'underscore';
import { Injectable } from '@angular/core';
import { Logger, MessageService } from '@core/lib/services';
import { DiQueue } from '@app/di/data-model/di-queue';
import { CurrentExamContextService } from '@app/common/current-exam-context.service';
import { XmlTemplateManagerService } from '@app/di/channel/lineshaft/xml-template-manager.service';
import { DiConfigService } from '@app/di/di-config.service';
import { SignOutService } from '@app/common/sign-out.service';
import { LsMessage } from '@app/di/data-model/ls-message';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { ResponseInfo } from '@app/di/data-model/response-info';
import { IntegrationStatusService } from '@app/di/integration-status.service';

const log = new Logger('DiService');

@Injectable({
  providedIn: 'root'
})
export class DiService {
  LoadMessageQueueParams = new DiQueue();

  private uvUpdater = null;
  private integrationStatus = false;
  private loadMessageQueue = null;
  private diDisableCallback;
  private queueMessageType = { LOAD: '0', SETCONTEXT: '1' };
  private OWNERINSTANCE;
  private logoutInitiatedStatus = false;
  private loadTimeout = null;
  errorCodeList;

  private UVIEWER_CONFIG;
  private WFM_CONTEXT_CONFIG;
  private APPLAUNCHER_CONFIG;
  private VIEWERCONTEXT_CONFIG;

  constructor(
    private messageService: MessageService,
    private notify: NotifyService,
    private translate: TranslateService,
    private diConfigService: DiConfigService,
    private currentExamContext: CurrentExamContextService,
    private signOutService: SignOutService,
    private integrationStatusService: IntegrationStatusService,
    private xmlTemplateManagerService: XmlTemplateManagerService
  ) {}

  initialize() {
    this.UVIEWER_CONFIG = this.diConfigService.UVIEWER_CONFIG;
    this.WFM_CONTEXT_CONFIG = this.diConfigService.WFM_CONTEXT_CONFIG;
    this.APPLAUNCHER_CONFIG = this.diConfigService.getAppLauncherConfig();
    this.VIEWERCONTEXT_CONFIG = this.diConfigService.VIEWER_CONTEXT_CONFIG;

    this.OWNERINSTANCE = this.diConfigService.REPORTING_CONFIG.OWNERINSTANCE;
  }

  getLSMessageType() {
    return this.queueMessageType;
  }

  setLogoutInitiatedStatus() {
    this.logoutInitiatedStatus = true;
  }

  isLogoutInitiated() {
    return this.logoutInitiatedStatus;
  }

  getLSMessage(type, xml) {
    return new LsMessage(type, xml);
  }

  setUVUpdater(_uvUpdater) {
    this.uvUpdater = _uvUpdater;
  }

  getUVUpdater() {
    return this.uvUpdater;
  }

  setIntegrationStatus(status) {
    this.integrationStatus = status;
  }

  isSupportedBrowser() {
    return this.integrationStatus;
  }

  getApplicationContext() {
    return this.currentExamContext.getContext() || null;
  }

  setApplicationContext(context) {
    this.currentExamContext.setContext(context);
  }

  setLoadMessageQueue(loadQueue) {
    this.loadMessageQueue = loadQueue;
  }

  getLoadMessageQueue() {
    return this.loadMessageQueue;
  }

  setDIDisableCallback(callback) {
    this.diDisableCallback = callback;
  }

  invokeAPIWrapperExecute(xml) {
    try {
      log.debug('invokeAPIWrapperExecute: ' + xml);
      // Subscribe response from LineShaftPluginManager to get the errors related to LineShaft.exe.
      // Subscribe LS_PLUGIN_SETCONTEXT_RESPONSE from LineShaftPluginManager
      let responseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
        this.OWNERINSTANCE + '.LS_PLUGIN_SETCONTEXT_RESPONSE',
        'LineShaftPluginManager'
      );
      this.uvUpdater.InvokeAPI(responseFromPluginManager);
      // Subscribe CTX_CTRL_MESSAGE_RESPONSE from LineShaftPluginManager
      responseFromPluginManager = this.xmlTemplateManagerService.getSubscribeXml(
        this.OWNERINSTANCE + '.CTX_CTRL_MESSAGE_RESPONSE',
        'LineShaftPluginManager'
      );
      this.uvUpdater.InvokeAPI(responseFromPluginManager);

      this.uvUpdater.InvokeAPI(xml);
    } catch (exception) {
      log.error('Error calling InvokeAPI - description: ' + exception.description);
      // Notify user if logout not initiated already
      if (!this.isLogoutInitiated()) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.lineShaftFailed'),
          this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
        );
        this.diDisableCallback();
        const signoutTimeout = setTimeout(() => {
          this.signOutService.signOut();
          clearTimeout(signoutTimeout);
        }, 10000);
      }
    }
  }

  invokeAPI(xml) {
    try {
      log.debug('invokeAPIWrapperExecute: ' + xml);
      this.uvUpdater.InvokeAPI(xml);
    } catch (exception) {
      log.error('Error calling InvokeAPI - description: ' + exception.description);
      // Notify user if logout not initiated already
      if (!this.isLogoutInitiated()) {
        this.notify.critical(
          this.translate.instant('di.desktopIntegration.critical.lineShaftFailed'),
          this.translate.instant('di.desktopIntegration.titleDesktopIntegrationDisabled')
        );
        this.diDisableCallback();
        const signoutTimeout = setTimeout(() => {
          this.signOutService.signOut();
          clearTimeout(signoutTimeout);
        }, 10000);
      }
    }
  }

  /**
   * This method handles invokeAPI request from loadPlugins() method.
   * Exception handling is part of loadPlugins() method
   */
  loadPluginsInvokeAPIWrapperExecute(xml) {
    // Call InvokeAPI for given xml
    log.debug('loadPluginsInvokeAPIWrapperExecute: ' + xml);
    try {
      this.uvUpdater.InvokeAPI(xml);
    } catch (exception) {
      // Exception throws on InvokeAPI call, if LineShaft registration fails. The description details in
      // catch seems to be getting just error, assuming LineShaft registration fails.
      log.error(
        'Error calling InvokeAPI - possible cause: LineShaft registration failure ' + '- description: ' + exception
      );
    }
  }

  hasXMLConfError(pluginType) {
    const pluginError = {};
    const itemToSearch = 'type';
    if (!_.isUndefined(pluginType)) {
      pluginError[itemToSearch] = pluginType;
      if (!_.isUndefined(this.errorCodeList)) {
        const errorListResult = _.findWhere(this.errorCodeList, pluginError);
        log.info('hasXMLConfError() - errorListResult : ' + JSON.stringify(errorListResult));
        return !_.isUndefined(errorListResult);
      } else {
        log.info('hasXMLConfError() - XML error list is empty');
        return false;
      }
    }
  }

  loadPluginsInvokeAPIWrapper(lsMessage) {
    if (this.LoadMessageQueueParams.messageWaitingForResponse === undefined) {
      log.info('calling loadPluginsInvokeAPIWrapperExecute from loadPluginsInvokeAPIWrapper');
      this.LoadMessageQueueParams.messageWaitingForResponse = lsMessage;
      this.loadPluginsInvokeAPIWrapperExecute(lsMessage.message);
      this.setLoadTimeout();
    } else {
      // put on queue
      this.LoadMessageQueueParams.messageQueue.push(lsMessage);
      log.info(
        'loadPluginsInvokeAPIWrapper - Add to queue - queue count: ' + this.LoadMessageQueueParams.messageQueue.length
      );
    }
  }

  setLoadTimeout() {
    log.debug('setLoadTimeout : setting timeout for load response : ' + this.UVIEWER_CONFIG.LS_PLUGINLOAD_TIMEOUT);
    // Timeout when load response from plugin is pending
    this.loadTimeout = setTimeout(() => {
      if (this.LoadMessageQueueParams.messageWaitingForResponse !== undefined) {
        if (
          this.LoadMessageQueueParams.messageWaitingForResponse.moduleName === this.UVIEWER_CONFIG.MODULENAME &&
          !this.integrationStatusService.getUViewerPluginStatus()
        ) {
          log.error('setLoadTimeout : timeout occurred disabling the viewer integration');
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.critical.loadViewerFailed'),
            this.translate.instant('di.desktopIntegration.titleViewerDisabled')
          );
          this.integrationStatusService.setUViewerPluginStatus(true);
          this.integrationStatusService.setViewerHealthStatus(
            this.integrationStatusService.healthStatusEnum.failed,
            true
          );
        } else if (
          this.LoadMessageQueueParams.messageWaitingForResponse.moduleName === this.VIEWERCONTEXT_CONFIG.MODULENAME &&
          !this.integrationStatusService.getUViewerNavSyncPluginStatus()
        ) {
          log.error('setLoadTimeout : timeout occurred disabling the ViewerContext integration');
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.critical.loadViewerContextPluginFailed'),
            this.translate.instant('di.desktopIntegration.titleViewerContextPluginDisabled')
          );
          this.integrationStatusService.setUViewerNavSyncPluginStatus(true);
          this.integrationStatusService.setViewerSyncHealthStatus(
            this.integrationStatusService.healthStatusEnum.failed
          );
        } else if (
          this.LoadMessageQueueParams.messageWaitingForResponse.moduleName === this.APPLAUNCHER_CONFIG.MODULENAME &&
          !this.integrationStatusService.getAppLauncherPluginStatus()
        ) {
          log.error('setLoadTimeout : timeout occurred disabling the app launcher integration');
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.critical.loadAppLauncherFailed'),
            this.translate.instant('di.desktopIntegration.titleAppLauncherDisabled')
          );
          this.integrationStatusService.setAppLauncherPluginStatus(true);
          this.integrationStatusService.setAppLauncherHealthStatus(
            this.integrationStatusService.healthStatusEnum.failed
          );
        } else if (
          this.LoadMessageQueueParams.messageWaitingForResponse.moduleName === this.WFM_CONTEXT_CONFIG.MODULENAME &&
          !this.integrationStatusService.getWFMContextPluginStatus()
        ) {
          log.error('setLoadTimeout : timeout occurred disabling the wfm context launcher integration');
          this.notify.critical(
            this.translate.instant('di.desktopIntegration.critical.loadGenExFailed'),
            this.translate.instant('di.desktopIntegration.titleGenExDisabled')
          );
          this.integrationStatusService.setWFMContextPluginStatus(true);
        } else {
          log.error(
            'setLoadTimeout : timeout occurred for module : ' +
              this.LoadMessageQueueParams.messageWaitingForResponse.moduleName
          );
          this.messageService.broadcast(
            'diLoadTimeout',
            this.LoadMessageQueueParams.messageWaitingForResponse.moduleName
          );
        }
      }
      this.LoadMessageQueueParams.messageWaitingForResponse = undefined;
      this.processQueue();
    }, this.UVIEWER_CONFIG.LS_PLUGINLOAD_TIMEOUT);
  }

  clearTimeout() {
    clearTimeout(this.loadTimeout);
  }

  processQueue() {
    if (this.LoadMessageQueueParams.messageWaitingForResponse === undefined) {
      const lsMessage =
        this.LoadMessageQueueParams.messageQueue.length > 0 ? this.LoadMessageQueueParams.messageQueue.shift() : null;
      if (lsMessage !== null) {
        this.LoadMessageQueueParams.messageWaitingForResponse = lsMessage;
        if (lsMessage.type === this.getLSMessageType().LOAD) {
          log.info('Process Queue - calling loadPluginsInvokeAPIWrapperExecute from processQueue');
          this.loadPluginsInvokeAPIWrapperExecute(lsMessage.message);
          this.setLoadTimeout();
        }
      } else {
        this.messageService.broadcast('processQueue');
        log.info('Process Queue - no message, Process SetContext Requests in Queue');
      }
    }
  }

  parseResponseXML(responseXML) {
    const responseInfo = new ResponseInfo();
    try {
      const parser = new DOMParser();
      let xmlDoc = parser.parseFromString(responseXML, 'text/xml');
      // Read study ID, index and result from response XML
      const topicNode = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'topic');
      responseInfo.topicName = topicNode[0].childNodes.item(1).childNodes[0].nodeValue;
      const sourceNode = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'source');
      responseInfo.sourceName = sourceNode.item(0).textContent;
      responseInfo.dataNodes = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'data');

      let nameNode = '';
      let nodeValue = '';
      let i = 0;
      // For Primordial Parsing
      if (responseInfo.topicName === 'LS_WFMCONTEXT_REQUEST') {
        this.parseGExInboundXml(responseInfo);
      } else {
        for (i = 0; i < responseInfo.dataNodes.length; i++) {
          // Take the name nodes from the xml
          nameNode = responseInfo.dataNodes[i].childNodes.item(1).childNodes[0].nodeValue;
          if (nameNode === 'PAYLOAD') {
            // Take the payload from the xml.
            const payload = responseInfo.dataNodes[i].childNodes.item(9).childNodes[0].nodeValue;
            // Parse the payload xml
            xmlDoc = parser.parseFromString(payload, 'text/xml');
            const payloadDataNodes = xmlDoc.getElementsByTagNameNS('http://ws.dotmed.gehc.com/dataexchange/', 'data');
            this.parsePayloadNode(payloadDataNodes, responseInfo);
          }
          if (
            responseInfo.dataNodes[i].childNodes.item(9) !== null &&
            responseInfo.dataNodes[i].childNodes.item(9).childNodes[0] !== undefined
          ) {
            nodeValue = responseInfo.dataNodes[i].childNodes.item(9).childNodes[0].nodeValue;
            if (nameNode === 'SCAN_DOC_STATUS') {
              responseInfo.docScanStatus = nodeValue;
            }
            if (nameNode === 'STATUS') {
              responseInfo.reportStatus = nodeValue;
            }
            if (nameNode === 'ADDENDUM') {
              responseInfo.addendumStatus = nodeValue;
            }
            if (nameNode === 'ACCESSION_NUMBER') {
              responseInfo.accessionInSaveEvent = nodeValue;
            }
            if (nameNode === 'ACCESSIONNUMBER') {
              responseInfo.accession = nodeValue;
            }
            if (nameNode === 'QUERY_RESULT') {
              responseInfo.queryResult = nodeValue;
            }
            if (nameNode === 'SITENAME') {
              responseInfo.siteNameInSaveEvent = nodeValue;
            }
            if (nameNode === 'ACTIONTYPE' || nameNode === 'ACTION_TYPE' || nameNode === 'ACTIONTYPE_INEVENT') {
              responseInfo.actionType = nodeValue;
            }
            if (nameNode === 'MRN') {
              responseInfo.mrn = nodeValue;
            }
            if (nameNode === 'DOMAIN') {
              responseInfo.domain = nodeValue;
            }
            if (nameNode === 'JWT') {
              responseInfo.jwtToken = nodeValue;
            }
            if (nameNode === 'MESSAGE_ID') {
              responseInfo.messageId = nodeValue;
            }

            if (nameNode === 'PATIENT_NAME') {
              responseInfo.patientName = nodeValue;
            }
            if (nameNode === 'RESULT') {
              responseInfo.resultValue = nodeValue;
            }
            if (nameNode === 'LOGINUSERNAME' || nameNode === 'USERNAME') {
              responseInfo.userName = nodeValue;
            }
            if (nameNode === 'OPENREPORTACCESSION_NUMBER') {
              responseInfo.accession = nodeValue;
            }
            if (nameNode === 'OPENREPORT_ASSOCIATE_ACCESSION_NUMBER') {
              responseInfo.associatedAccession = nodeValue;
            }

            if (nameNode === 'APPLICATION_NAME') {
              responseInfo.applicationName = nodeValue;
            }
            if (nameNode === 'OPENREPORT_SITENAME') {
              responseInfo.siteName = nodeValue;
            }
            if (nameNode === 'PS_STATE') {
              responseInfo.state = nodeValue;
            }
            if (nameNode === 'ACTION_STATUS') {
              responseInfo.actionStatus = nodeValue;
            }
            if (nameNode === 'FAILURE_TYPEINEVENT') {
              responseInfo.failureType = nodeValue;
            }
            if (nameNode === 'FAILED_ACTIONINEVENT') {
              responseInfo.dictationFailedAction = nodeValue;
            }
            if (nameNode === 'RESULT_MSG') {
              responseInfo.resultMsg = nodeValue;
            }

            if (nameNode === 'FAILED_ACTION') {
              responseInfo.dictationFailedAction = nodeValue;
            }
            if (nameNode === 'FAILURE_TYPE') {
              responseInfo.failureType = nodeValue;
            }
            if (nameNode === 'MODAL_DIALOG') {
              responseInfo.modalDialog = true;
            }
            if (nameNode === 'ERROR_CODE') {
              responseInfo.errorCode = nodeValue;
            }
            if (nameNode === 'DESTINATION') {
              responseInfo.destination = nodeValue;
            }
            if (nameNode === 'EXAM_IDENT') {
              responseInfo.examIdent = nodeValue;
            }
            if (nameNode === 'VIEWER_CRASHED') {
              responseInfo.isUVCrashed = true;
            }
            if (nameNode === 'REPORTING_APPPATH') {
              responseInfo.reportingAppPath = nodeValue;
            }
            if (nameNode === 'CTXCONTROLLER') {
              responseInfo.contextController = nodeValue;
            }
            if (nameNode === 'CLOSED_REASON') {
              responseInfo.closedReason = nodeValue;
            }
          }
          if (nameNode === 'FAILURE_TYPES') {
            responseInfo.failureTypes = this.updateFailuresResponseInfo(responseInfo.dataNodes[i].childNodes);
          }
          if (nameNode === 'LVEC_LOGIN_FAILED_REASON') {
            const failArray = this.updateFailuresResponseInfo(responseInfo.dataNodes[i].childNodes);
            responseInfo.failureTypes = responseInfo.failureTypes.concat(failArray);
          }
        }
      }
    } catch (error) {
      log.error('ParseResponseXML failed. Error = ' + error);
    }
    return responseInfo;
  }

  parsePayloadNode(payloadDataNodes: any, responseInfo) {
    let studyIndex;
    for (const payloadDataNode of payloadDataNodes) {
      const nodeName = payloadDataNode.childNodes.item(1).childNodes[0].nodeValue;
      let nodeValue = null;
      if (payloadDataNode.childNodes.item(9) !== null) {
        if (payloadDataNode.childNodes.item(9).childNodes[0] !== undefined) {
          nodeValue = payloadDataNode.childNodes.item(9).childNodes[0].nodeValue;
        }
      }

      if (nodeName === 'CURRENT_CONTEXT_INDEX') {
        // Take the current Context Index
        studyIndex = nodeValue;
        responseInfo.currentIndex = nodeValue;
      }
      if (nodeName === 'EXAM_IDENT') {
        // Take the study id corresponding to the index.
        for (let index = 9, count = 0; count <= studyIndex; index = index + 2, count++) {
          if (payloadDataNode.childNodes.item(index) !== null) {
            responseInfo.examIdent = payloadDataNode.childNodes.item(index).childNodes[0].nodeValue;
          }
        }
        if (responseInfo.examIdent !== '') {
          // Extract the study id from exam identifier. - should have been done in the parseXML?
          const str = responseInfo.examIdent.split(',')[0].split(':');
          responseInfo.examIdent = str.length > 1 ? str[1] : str[0];
        }
      }

      if (nodeName === 'ACCESSION_NUMBER') {
        responseInfo.accession = nodeValue;
      }
      if (nodeName === 'STATE') {
        responseInfo.state = nodeValue;
      }
      if (nodeName === 'ACTIONTYPE') {
        responseInfo.actionType = nodeValue;
      }
      if (nodeName === 'SITENAME') {
        responseInfo.siteName = nodeValue;
      }
      if (nodeName === 'USERNAME') {
        responseInfo.userName = nodeValue;
      }
      if (nodeName === 'MODULENAME') {
        responseInfo.moduleName = nodeValue;
      }
      if (nodeName === 'DONE_FLAGS' || nodeName === 'CLOSEOPTIONS') {
        responseInfo.isDiscardXML = true;
      }
    }
  }

  parseGExInboundXml(responseInfo) {
    for (const dataNode of responseInfo.dataNodes) {
      if (dataNode.childNodes.item(9) !== null && dataNode.childNodes.item(9).childNodes[0] !== undefined) {
        const nameNode = dataNode.childNodes.item(1).childNodes[0].nodeValue;
        const nodeValue = dataNode.childNodes.item(9).childNodes[0].nodeValue;
        if (nameNode === 'EXAM_IDENT') {
          responseInfo.accession = nodeValue.split('ACC:')[1];
          responseInfo.examIdent = nodeValue;
        }
        if (nameNode === 'WFM_CONTEXT_ACTION_TYPE') {
          responseInfo.contextActionType = nodeValue;
        }
        if (nameNode === 'CONTEXT_RESPONSE_OWNER') {
          responseInfo.contextResponseOwner = nodeValue;
        }
      }
    }
  }

  updateFailuresResponseInfo(childNodes) {
    let failureTypeIterator = 0;
    const failureCount = parseInt(childNodes.item(5).childNodes[0].nodeValue, 10);
    const failureArray = [];
    // Add the failure types in to an array
    for (let iterator = 9; failureTypeIterator < failureCount; iterator = iterator + 2) {
      failureArray[failureTypeIterator] = childNodes.item(iterator).childNodes[0].nodeValue;
      failureTypeIterator = failureTypeIterator + 1;
    }
    return failureArray;
  }
}
